import { Component, Input, Inject, LOCALE_ID, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule, Location, DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DocumentsDialog } from '../documents-dialog/documents-dialog.component';
import { NotesComponent } from '../notes-dialog/notes.component';
import { BidDialogComponent } from '../bid-dialog/bid-dialog.component';
import { DeclineDialogComponent } from '../decline-dialog/decline-dialog.component';
import { SignAtpDialogComponent } from '../sign-atp-dialog/sign-atp-dialog.component';
import { SubmitForReviewDialogComponent } from '../submit-for-review-dialog/submit-for-review-dialog.component';
import { ProjectService } from '../services/project.service';
import { getStoredUser } from '../helpers/user-storage.util';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { SiteService } from '../services/site.service';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ProposalService } from '../services/proposal.service';
import { FormsModule } from '@angular/forms';
import { AtpSignAcceptedDialogComponent } from '../atp-sign-accepted-dialog/atp-sign-accepted-dialog.component';
import { DeclineSuccessComponent } from '../decline-success/decline-success.component';
import { GoogleMapsModule } from '@angular/google-maps';
import { DocumentService } from '../services/document.service';
import { PdfComponent } from '../pdf-viewer/pdf-viewer.component';
import { MatDialog } from '@angular/material/dialog';
import { AcceptOfferedProposalDialogComponent } from '../accept-offered-proposal-dialog/accept-offered-proposal-dialog.component';
import { AcceptedOfferedProposalDialogComponent } from '../accepted-offered-proposal-dialog/accepted-offered-proposal-dialog.component';
import { SiteVisitComponent } from '../site-visit/site-visit-detail/site-visit.component';
import { ExpensesDialogComponent } from '../expenses-dialog/expenses-dialog.component';
import { GoogleMapsLoaderService } from '../services/google-maps-loader.service';

@Component({
  standalone: true,
  selector: 'proposal-details',
  imports: [
    CommonModule,
    DocumentsDialog,
    NotesComponent,
    BidDialogComponent,
    DeclineDialogComponent,
    SignAtpDialogComponent,
    SubmitForReviewDialogComponent,
    AtpSignAcceptedDialogComponent,
    DeclineSuccessComponent,
    FormsModule,
    GoogleMapsModule,
    AcceptOfferedProposalDialogComponent,
    AcceptedOfferedProposalDialogComponent,
    SiteVisitComponent,
    ExpensesDialogComponent,
  ],
  templateUrl: './proposal-details.component.html',
  styleUrl: './proposal-details.component.scss',
  providers: [DatePipe],
})
export class ProposalDetails implements OnDestroy {
  private mapsLoadedSub: Subscription;

  @Input() address: any = { streetAddress: '', cityStateZip: '' };
  @Input() siteType: string = '';
  @Input() siteSubType: string = '';
  @Input() documents: any = [];
  @Input() modalHeaderTitle: string = '';
  @Input() modalBody: string = '';
  @Input() atpDocumentPath: SafeResourceUrl = '';
  @Input() isDeclineSuccessActive: boolean = false;
  @Input() bidNotes: string = '';
  @Input() rowIndex: number = 0;

  id: string = '';
  data: any = {};
  projectName: string = '';
  activeTab: string = 'rfp-info';
  type: string = '';
  activeBreadcrumb: any = [false, false, false, false, false];
  currentConversation: string = '';
  documentsActive: boolean = false;
  notesActive: boolean = false;
  bidActive: boolean = false;
  isDeclineActive: boolean = false;
  signAtpActive: boolean = false;
  submitForReviewActive: boolean = false;
  expensesActive: boolean = false;
  showReviewSubmission: boolean = false;
  showRevisionSubmission: boolean = false;
  showTableA: boolean = false;
  isAlta: boolean = false;
  isFixedPrice: boolean = false;
  user = getStoredUser();
  tableA: string = '';
  tableAItemName: string = '';
  tableAItemValue: string = '';
  item20OrItem21: string = '';
  containsItem21: boolean = false;
  project: any = null;
  atpDocument: any;
  bidAmount: number = 0;
  turnTime: number = 0;
  proposedDeliverDate: any = null;
  isATPSignedAccepted: boolean = false;
  isAtp: boolean = false; // for decline dialog
  childModalHeaderTitle: string = 'ATP Signed & Project Active';
  childModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';
  isOfferAcceptedActive: boolean = false;
  isOfferAcceptedConfirmationActive: boolean = false;
  status: string = '';
  selectedSiteVisitDate: any = '';
  selectedSiteVisitSlot: any = '';
  submitBidDisabled: boolean = false;
  siteDetail: any = '';
  desiredByDate: Date = new Date();
  isDesiredByDate: boolean = false;

  // google map
  center: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 };
  zoom: number = 4;
  mapOptions: google.maps.MapOptions = {};
  polygonPaths: google.maps.LatLngLiteral[] = [];
  polygonOptions: google.maps.PolygonOptions = {
    fillColor: '#6296ff',
    fillOpacity: 0.25,
    strokeColor: '#537ed5',
    strokeOpacity: 1,
    strokeWeight: 3,
    clickable: false,
    editable: false,
    draggable: false,
  };

  formattedDate: string = '';
  minDate: Date = new Date();
  mapsLoaded: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private utilityService: CommonUtilityServiceService,
    private siteService: SiteService,
    private proposalService: ProposalService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private documentService: DocumentService,
    private dialog: MatDialog,
    private mapsLoader: GoogleMapsLoaderService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.mapsLoadedSub = this.mapsLoader.loaded$.subscribe((loaded) => {
      this.mapsLoaded = loaded;
      if (loaded && this.siteDetail) {
        this.initMap();
      }
    });
  }

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.type = data['type'];
    });
    this.route.paramMap.subscribe((params) => {
      this.id = params.get('id') || '';
      this.projectService.fetchData(this.user.id ?? '').subscribe({
        next: (result) => {
          this.data = result
            .map((parent: any) => ({
              ...parent,
              sites: parent.sites.filter(
                (child: any) => child.site?.id === this.id
              ),
            }))
            .filter(
              (parent: any) => parent.sites.length > 0 && parent.projectId
            );
          this.siteDetail = this.data[0].sites[0];
          // setting alta related variables
          this.showTableA =
            this.data[0].sites[0].site.serviceType === 'NEXUS_ALTA_SURVEY';
          this.isAlta = this.showTableA;
          // setting status and breadcrumbs
          if (this.data[0]) {
            this.status = this.data[0]?.sites[0]?.proposal?.status;
            this.isFixedPrice = this.data[0]?.sites[0]?.proposal?.fixedAmount;
            if (this.data[0]?.sites[0]?.site?.desiredByDate) {
              this.desiredByDate = this.data[0]?.sites[0]?.site?.desiredByDate;
              this.isDesiredByDate = true;
            }
            this.address = this.utilityService.formatAddress(
              this.data[0].sites[0]
            );
            this.containsItem21 = this.data[0].sites[0].site.tableA?.some(
              (item: any) => item.itemName === '21'
            );
            let response = this.utilityService.formatTableA(
              this.data[0].sites[0],
              this.containsItem21
            );
            if (response) {
              this.item20OrItem21 = response[0];
              this.tableA = response[1];
            }
            this.dataConversations();
            if (this.type === 'invitation') {
              // after bid is submitted
              if (this.data[0].sites[0].proposal.status === 'SUBMITTED') {
                this.activeBreadcrumb[1] = true;
                this.activeBreadcrumb[0] = false;
              } else {
                this.activeBreadcrumb[0] = true;
              }
            }
            if (this.type === 'proposal') {
              // after signing atp, status will be active
              if (
                this.data[0].sites[0].proposal.status === 'ACTIVE' ||
                this.data[0].sites[0].proposal.status === 'BID_LOST' ||
                this.data[0].sites[0].proposal.status === 'NO_RESPONSE'
              ) {
                this.activeBreadcrumb[2] = true;
                this.activeBreadcrumb[1] = false;
              } else {
                this.activeBreadcrumb[1] = true;
              }
            }
            if (this.type === 'active') {
              if (
                this.data[0].sites[0].proposal.status === 'REVIEW_PENDING' ||
                this.data[0].sites[0].proposal.status ===
                  'REVISION_REQUESTED' ||
                this.data[0].sites[0].proposal.status === 'REVISION_PENDING' ||
                this.data[0].sites[0].proposal.status === 'IN_REVIEW'
              ) {
                this.activeBreadcrumb[3] = true;
                this.activeBreadcrumb[2] = false;
              } else if (
                this.data[0].sites[0].proposal.status === 'COMPLETED'
              ) {
                this.activeBreadcrumb[4] = true;
                this.activeBreadcrumb[3] = false;
              } else {
                this.activeBreadcrumb[2] = true;
              }
            }
            if (this.type == 'project-overview') {
              if (
                this.data[0].sites[0].proposal.status === 'INVITATION' ||
                this.data[0].sites[0].proposal.status === 'OFFERED'
              ) {
                this.activeBreadcrumb[0] = true;
              } else if (
                this.data[0]?.sites[0]?.proposal?.status === 'PROPOSAL' ||
                this.data[0]?.sites[0]?.proposal?.status === 'SUBMITTED' ||
                this.data[0]?.sites[0]?.proposal?.status === 'BID_WON'
              ) {
                this.activeBreadcrumb[1] = true;
                this.activeBreadcrumb[0] = false;
              } else if (
                this.data[0].sites[0].proposal.status === 'BID_LOST' ||
                this.data[0].sites[0].proposal.status === 'NO_RESPONSE'
              ) {
                this.activeBreadcrumb[2] = true;
                this.activeBreadcrumb[1] = false;
              } else if (this.data[0].sites[0].proposal.status === 'ACTIVE') {
                this.activeBreadcrumb[2] = true;
              } else if (
                this.data[0].sites[0].proposal.status === 'REVIEW_PENDING' ||
                this.data[0].sites[0].proposal.status ===
                  'REVISION_REQUESTED' ||
                this.data[0].sites[0].proposal.status === 'REVISION_PENDING' ||
                this.data[0].sites[0].proposal.status === 'IN_REVIEW'
              ) {
                this.activeBreadcrumb[3] = true;
                this.activeBreadcrumb[2] = false;
              } else if (
                this.data[0].sites[0].proposal.status === 'COMPLETED'
              ) {
                this.activeBreadcrumb[4] = true;
                this.activeBreadcrumb[3] = false;
              }
            }
            if (
              this.type === 'project-history' &&
              this.data[0].sites[0].proposal.status === 'COMPLETED'
            ) {
              this.activeBreadcrumb[4] = true;
              this.activeBreadcrumb[3] = false;
            }
            this.bidAmount = this.data[0].sites[0].proposal.bid;
            this.turnTime = this.data[0].sites[0].proposal.proposedTurnTime;
            this.bidNotes = this.data[0].sites[0].proposal.bidNotes;
            this.proposedDeliverDate =
              this.data[0].sites[0].proposal.proposedDeliverDate;
            if (
              this.bidAmount > 0 &&
              (this.turnTime > 0 || this.proposedDeliverDate)
            ) {
              this.submitBidDisabled = false;
            } else {
              this.submitBidDisabled = true;
            }
            // set site type and site subtype
            this.siteType = this.data[0].sites[0].site.siteType;
            this.siteSubType = this.data[0].sites[0].site.siteSubType;
            // configure map
            if (this.mapsLoaded) {
              this.initMap();
            }
            // set documents
            let documents: any = [];
            if (this.data[0].sites[0].site.documents) {
              documents = [
                ...documents,
                ...this.data[0].sites[0].site.documents,
              ];
            }
            if (this.data[0].sites[0].proposal.documents) {
              documents = [
                ...documents,
                ...this.data[0].sites[0].proposal.documents,
              ];
              this.showReviewSubmission =
                !this.isAlta ||
                this.data[0].sites[0].proposal.documents.some(
                  (document: { type: string }) =>
                    document.type === 'DELIVERABLE'
                );
              this.showRevisionSubmission =
                !this.isAlta ||
                this.data[0].sites[0].proposal.documents.some(
                  (document: { type: string }) => document.type === 'REVISION'
                );
            }
            this.documents = documents;
            // set delivery date
            if (this.data[0].sites[0].proposal.proposedDeliverDate) {
              this.proposedDeliverDate = new Date(
                this.data[0].sites[0].proposal.proposedDeliverDate
              );
            }
            if (this.data[0].sites[0].proposal.selectedSiteVisit) {
              this.selectedSiteVisitDate = new Date(
                this.data[0].sites[0].proposal.selectedSiteVisit.date
              );
              this.selectedSiteVisitSlot =
                this.data[0].sites[0].proposal.selectedSiteVisit.slot;
            }
          }
        },
      });
    });
  }

  private initMap(): void {
    this.mapOptions = {
      mapTypeId: google.maps.MapTypeId.HYBRID,
      mapTypeControl: false,
    };
    this.center = this.utilityService.determineMapCenter(
      this.siteDetail.site.parcels
    );
    this.zoom = this.utilityService.determineMapZoomLevel(
      this.siteDetail.site.totalAcreage
    );
    this.polygonPaths = this.utilityService.detemineMapBounderies(
      this.siteDetail
    );
  }

  ngOnDestroy(): void {
    this.mapsLoadedSub.unsubscribe();
  }

  dataConversations() {
    if (this.data.conversations) {
      // order messages by date
      for (let conversation of this.data.conversations) {
        conversation.messages.sort((a: any, b: any) => {
          let res = 0;
          let aDate = new Date(a.date);
          let bDate = new Date(b.date);
          if (aDate < bDate) res = -1;
          if (aDate > bDate) res = 1;
          return res;
        });
      }

      // order conversations by date of last message
      this.data.conversations.sort((a: any, b: any) => {
        let res = 0;
        let aDate = new Date(a.messages[a.messages.length - 1].date);
        let bDate = new Date(b.messages[b.messages.length - 1].date);
        if (aDate < bDate) res = 1;
        if (aDate > bDate) res = -1;
        return res;
      });

      this.currentConversation = this.data.conversations[0].id;
    }
  }

  goBack() {
    window.history.back();
  }

  changeConversation(conversation: string) {
    this.currentConversation = conversation;
  }

  toggleTab(tab: string) {
    this.activeTab = tab;
  }

  toggleDocumentsModal() {
    this.documentsActive = !this.documentsActive;
  }

  toggleNotesModal() {
    this.notesActive = !this.notesActive;
  }

  toggleSubmitBidModal() {
    this.bidActive = !this.bidActive;
  }

  toggleATPModal() {
    this.signAtpActive = !this.signAtpActive;
  }

  toggleAtpAcceptedModal() {
    this.isATPSignedAccepted = !this.isATPSignedAccepted;
  }

  toggleOpenDeclineModal(isAtp: boolean) {
    this.isAtp = isAtp;
    this.isDeclineActive = true;
  }

  toggleCloseDeclineModal() {
    this.isDeclineActive = false;
  }

  toggleDeclineSucessModal() {
    this.isDeclineSuccessActive = !this.isDeclineSuccessActive;
  }

  toggleSubmitForReviewModal() {
    this.submitForReviewActive = !this.submitForReviewActive;
  }

  toggleOfferAcceptedModal() {
    this.isOfferAcceptedActive = !this.isOfferAcceptedActive;
  }

  toggleOfferAcceptedConfirmationModal() {
    this.isOfferAcceptedConfirmationActive =
      !this.isOfferAcceptedConfirmationActive;
  }

  toggleExpensesModal() {
    this.expensesActive = !this.expensesActive;
  }

  sendMessage() {
    alert('send message');
  }

  submitBid() {
    this.toggleSubmitBidModal();
  }

  submitForReview() {
    this.toggleSubmitForReviewModal();
  }

  saveTurnTime() {
    const siteDetail = this.data[0].sites[0];
    const projectDetail = this.data[0];
    if (this.turnTime !== siteDetail.proposal.proposedTurnTime) {
      const updatedProposal = {
        ...siteDetail.proposal,
        proposedTurnTime: this.turnTime,
      };
      this.proposalService.saveProposal(updatedProposal);
      const updatedSiteDetail = { ...siteDetail, proposal: updatedProposal };
      this.data = updatedSiteDetail;
      const updatedProjectDetail = {
        ...projectDetail,
        sites: [updatedSiteDetail],
      };
      this.projectService.updateProjectDataSubjectWithSingleSite(
        updatedProjectDetail
      );
    }
  }

  saveBidAmount() {
    const siteDetail = this.data[0].sites[0];
    const projectDetail = this.data[0];
    if (this.bidAmount !== siteDetail.proposal.bid) {
      const updatedProposal = { ...siteDetail.proposal, bid: this.bidAmount };
      this.proposalService.saveProposal(updatedProposal);
      const updatedSiteDetail = { ...siteDetail, proposal: updatedProposal };
      this.data = updatedSiteDetail;
      const updatedProjectDetail = {
        ...projectDetail,
        sites: [updatedSiteDetail],
      };
      this.projectService.updateProjectDataSubjectWithSingleSite(
        updatedProjectDetail
      );
    }
  }

  decline(event: {
    declineReason: string;
    declineOtherReason: string;
    declineComments: string;
  }) {
    const updatedProposal = {
      ...this.data[0].sites[0].proposal,
      declineReason: event.declineReason,
      declineOtherReason: event.declineOtherReason,
      declineComments: event.declineComments,
    };
    this.proposalService
      .declineInvitation(updatedProposal)
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const proposal = apiResponse.data;
          if (proposal) {
            this.toggleDeclineSucessModal();
            const updatedSiteDetail = {
              ...this.data[0].sites[0],
              proposal: proposal,
            };
            const updatedProjectDetail = {
              ...this.data[0],
              sites: [updatedSiteDetail],
            };
            this.projectService.updateProjectDataSubjectWithSingleSite(
              updatedProjectDetail
            );
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  generateATP() {
    this.toastr.info(
      'Please wait a moment while we generate your ATP document. This process may take a few seconds.'
    );
    let atpDocumentResponse: any = null;
    this.siteService
      .generateAtp(this.data[0], this.user.id ?? '')
      .subscribe((response) => {
        if (response) {
          atpDocumentResponse = response;
          if (
            atpDocumentResponse.status === 'SUCCESS' &&
            atpDocumentResponse.data
          ) {
            this.atpDocument = atpDocumentResponse.data;
            if (this.atpDocument.documentPath) {
              this.atpDocumentPath =
                this.sanitizer.bypassSecurityTrustResourceUrl(
                  this.atpDocument.documentPath
                );
              this.toggleATPModal();
            } else {
              this.toastr.error(
                'There was an issue with genereating your ATP document. Please try again.'
              );
            }
          } else {
            this.toastr.error(atpDocumentResponse.message);
          }
        } else {
          this.toastr.error(
            'There was an issue with genereating your ATP document. Please try again.'
          );
        }
      });
  }

  signATP(event: { fullName: string }) {
    this.toastr.info('Accepting your signed ATP...');
    this.siteService
      .signAtp(
        {
          projectDetail: this.data[0],
          atpDocument: this.atpDocument,
          atpSignedBy: event.fullName,
        },
        this.user.id ?? ''
      )
      .subscribe((response) => {
        const updatedProjectDetail = response.data;
        this.projectService.updateProjectDataSubjectWithProjectDetail(
          updatedProjectDetail
        );
        this.signAtpActive = false;
        this.toggleAtpAcceptedModal();
      });
  }

  declineATP(event: {
    declineReason: string;
    declineOtherReason: string;
    declineComments: string;
  }) {
    const siteDetail = this.data[0].sites[0];
    const updatedProposal = {
      ...siteDetail.proposal,
      declineReason: event.declineReason,
      declineOtherReason: event.declineOtherReason,
      declineComments: event.declineComments,
    };
    const updatedSiteDetail = { ...siteDetail, proposal: updatedProposal };
    this.siteService
      .cancel(this.user.id ?? '', {
        ...this.data[0],
        sites: [updatedSiteDetail],
      })
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedProjectDetail = response.data;
          if (updatedProjectDetail) {
            this.projectService.updateProjectDataSubjectWithProjectDetail(
              updatedProjectDetail
            );
            this.toggleDeclineSucessModal();
            this.isAtp = true;
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  submitReview() {
    this.proposalService
      .submitReadyForReview(this.data[0].sites[0].proposal.id)
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedProposal = response.data;
          if (updatedProposal) {
            const updatedSiteDetail = {
              ...this.data[0].sites[0],
              proposal: updatedProposal,
            };
            const updatedProjectDetail = {
              ...this.data[0],
              sites: [updatedSiteDetail],
            };
            this.projectService.updateProjectDataSubjectWithSingleSite(
              updatedProjectDetail
            );
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  submitRevision() {
    this.proposalService
      .submitRevision(this.data[0].sites[0].proposal.id)
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedProposal = response.data;
          if (updatedProposal) {
            const updatedSiteDetail = {
              ...this.data[0].sites[0],
              proposal: updatedProposal,
            };
            const updatedProjectDetail = {
              ...this.data[0],
              sites: [updatedSiteDetail],
            };
            this.projectService.updateProjectDataSubjectWithSingleSite(
              updatedProjectDetail
            );
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }

  saveBidNote(event: { fieldName: String; value: any }) {
    const siteDetail = this.data[0].sites[0];
    const projectDetail = this.data[0];
    if (event.value !== siteDetail.proposal.bidNotes) {
      const updatedProposal = {
        ...siteDetail.proposal,
        bidNotes: event.value,
      };

      this.proposalService.saveProposal(updatedProposal);

      const updatedSiteDetail = { ...siteDetail, proposal: updatedProposal };
      this.data = updatedSiteDetail;
      const updatedProjectDetail = {
        ...projectDetail,
        sites: [updatedSiteDetail],
      };
      this.projectService.updateProjectDataSubjectWithSingleSite(
        updatedProjectDetail
      );
    }
    this.toggleNotesModal();
  }

  updateDocuments(event: { siteDetail: any }) {
    const updatedProjectDetail = {
      ...this.data[0],
      sites: [event.siteDetail],
    };
    this.data = updatedProjectDetail;
    this.projectService.updateProjectDataSubjectWithSingleSite(
      updatedProjectDetail
    );
  }

  downloadDocument(document: any) {
    this.toastr.info('Downloading document...');
    this.documentService.downloadDocument(document);
  }

  openPdfViewer(document: any): void {
    this.documentService
      .downloadPdfToViewer(document)
      .then((result) => {
        const dialogRef = this.dialog.open(PdfComponent, {
          height: '90%',
          width: '50%',
          panelClass: 'pdf-viewer-dialog-container',
          data: { pdfSrc: result.url, filename: result.filename },
        });
      })
      .catch((error) => {
        this.toastr.error('Preview unavailable due to an error.');
      });
  }

  acceptOffer() {
    this.toggleOfferAcceptedModal();
    this.toggleOfferAcceptedConfirmationModal();
  }

  saveExpense(event: {
    travelCost: number;
    otherCost: number;
    otherCostDescription: string;
  }) {
    const siteDetail = this.data[0].sites[0];
    const projectDetail = this.data[0];
    // update proposal with new values
    const updatedProposal = {
      ...siteDetail.proposal,
      travelCost: event.travelCost,
      otherCost: event.otherCost,
      otherCostDescription: event.otherCostDescription,
    };
    // saving proposal
    this.proposalService.saveExpenses(updatedProposal);
    // updating the state
    const updatedSiteDetail = { ...siteDetail, proposal: updatedProposal };
    this.data = updatedSiteDetail;
    const updatedProjectDetail = {
      ...projectDetail,
      sites: [updatedSiteDetail],
    };
    this.projectService.updateProjectDataSubjectWithSingleSite(
      updatedProjectDetail
    );
  }
}
